/**
 * The consumer terms and the seller (MM-D-138, MM-D-139; TMMAPI-0156 U1): `consumerTermsVersion`,
 * `consumerTermsUrl`, `privacyPolicyUrl`, `sellerName` and `sellerLegalName`, served alike on
 * `/bootstrap`, `/me/billing` and the price book. A pure normalizer with no request, so the
 * bootstrap (which the HTTP client itself depends on) can use it.
 */
import type { BillingProfile as WireBillingProfile, BootstrapResponse } from './generated';

/** Who bills the person: the trade name and the legal name (MM-D-139). */
export interface Seller {
  /** The trade name; null when not served, or when it is the legal name. */
  name: string | null;
  legalName: string;
}

export interface ConsumerTerms {
  /** The terms' current version, which a subscribe and a first-sign-in consent accept. */
  version: string | null;
  /** Where the terms and the privacy policy live; null until the Operator sets them (plain text). */
  termsUrl: string | null;
  privacyUrl: string | null;
  /** Null when no seller is served: the "Billed by" line is omitted. */
  seller: Seller | null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** Only an absolute http(s) URL becomes a link: anything else is plain text, never an href. */
function linkUrl(value: unknown): string | null {
  const raw = text(value);
  if (raw === null) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

export function normalizeConsumerTerms(
  wire: Partial<BootstrapResponse> | Partial<WireBillingProfile> | Record<string, unknown>,
): ConsumerTerms {
  const w = (typeof wire === 'object' && wire !== null ? wire : {}) as Record<string, unknown>;
  const name = text(w.sellerName);
  const legalName = text(w.sellerLegalName) ?? name;
  return {
    version: text(w.consumerTermsVersion),
    termsUrl: linkUrl(w.consumerTermsUrl),
    privacyUrl: linkUrl(w.privacyPolicyUrl),
    seller:
      legalName === null
        ? null
        : { name: name !== null && name !== legalName ? name : null, legalName },
  };
}

export const NO_CONSUMER_TERMS: ConsumerTerms = {
  version: null,
  termsUrl: null,
  privacyUrl: null,
  seller: null,
};
